import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { ChatThread, ChatMessage, BrandSkill } from "../../../shared/types";

// ── Response shapes ──

interface SkillSummary {
  slug: string;
  name: string;
  phase: number;
  source: "default" | "brand";
  version: number;
  content_length: number;
}

interface SkillsListResponse {
  data: SkillSummary[];
}

interface SkillDetailResponse {
  data: {
    slug: string;
    name: string;
    content: string;
    phase: number;
    source: "default" | "brand";
    version: number;
  };
}

interface SkillsThreadResponse {
  data: { thread: ChatThread; messages: ChatMessage[] };
}

interface SendMessageResponse {
  data: {
    user_message: ChatMessage;
    assistant_message: ChatMessage;
    skill_edit: { slug: string; name: string; content: string } | null;
  };
}

interface ApplyEditResponse {
  data: BrandSkill;
}

// ── Queries ──

export function useSkills(brandId: string | undefined) {
  return useQuery({
    queryKey: ["skills", brandId],
    queryFn: () => api.get<SkillsListResponse>(`/api/skills/${brandId}`),
    enabled: !!brandId,
  });
}

export function useSkillDetail(brandId: string | undefined, slug: string | undefined) {
  return useQuery({
    queryKey: ["skills", brandId, slug],
    queryFn: () => api.get<SkillDetailResponse>(`/api/skills/${brandId}/${slug}`),
    enabled: !!brandId && !!slug,
  });
}

export function useSkillsThread(threadId: string | undefined) {
  return useQuery({
    queryKey: ["skills-threads", threadId],
    queryFn: () => api.get<SkillsThreadResponse>(`/api/skills/threads/${threadId}`),
    enabled: !!threadId,
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      if (!data) return false;
      const messages = data.messages;
      if (messages.length === 0) return false;
      const lastMessage = messages[messages.length - 1];
      return lastMessage?.role === "user" ? 2_000 : false;
    },
  });
}

// ── Mutations ──

export function useCreateSkillsThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { brand_id: string }) =>
      api.post<{ data: ChatThread }>("/api/skills/threads", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["skills-threads"] });
    },
  });
}

export function useSendSkillsMessage(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { content: string }) =>
      api.post<SendMessageResponse>(`/api/skills/threads/${threadId}/messages`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["skills-threads", threadId] });
    },
  });
}

export function useApplySkillEdit(threadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { skill_slug: string; skill_name: string; new_content: string }) =>
      api.post<ApplyEditResponse>(`/api/skills/threads/${threadId}/apply`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["skills"] });
      qc.invalidateQueries({ queryKey: ["skills-threads", threadId] });
    },
  });
}
